import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { LoadingButton, LoadingLinkButton } from "../components/Spinner";
import PasswordInput from "../components/PasswordInput";

type Mode = "signin" | "signup" | "forgot";

export default function Login() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const next = sp.get("next") ?? "/";
  const invite = sp.get("invite") ?? "";
  const googleStart = `/api/auth/google/start?next=${encodeURIComponent(next)}${
    invite ? `&invite=${encodeURIComponent(invite)}` : ""
  }`;

  const initialMode = ((): Mode => {
    const m = sp.get("mode");
    return m === "signup" || m === "forgot" ? m : "signin";
  })();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaNeeded, setMfaNeeded] = useState(false);

  const after = async () => {
    await qc.invalidateQueries({ queryKey: ["me"] });
    if (invite) await qc.invalidateQueries({ queryKey: ["workspaces"] });
    nav(next);
  };

  const login = useMutation({
    mutationFn: () => api.login({ email: email.trim().toLowerCase(), password, invite: invite || undefined, mfaCode: mfaNeeded ? mfaCode.trim() : undefined }),
    onSuccess: after,
    onError: (e) => {
      const m = String((e as Error).message ?? "");
      if (m.includes("mfa_required")) {
        setMfaNeeded(true);
        setErr(null);
      } else if (m.includes("bad_mfa_code")) {
        setErr("That code didn't match. Try the next one your authenticator shows.");
      } else {
        setErr(parseErr(e, "Couldn't sign in. Check email and password."));
      }
    },
  });
  const register = useMutation({
    mutationFn: () => api.register({ email: email.trim().toLowerCase(), password, name: name.trim() || undefined, invite: invite || undefined }),
    onSuccess: after,
    onError: (e) => setErr(parseErr(e, "Couldn't create an account.")),
  });
  const forgot = useMutation({
    mutationFn: () => api.forgotPassword(email.trim().toLowerCase()),
    onSuccess: () => setForgotSent(true),
    onError: (e) => setErr(parseErr(e, "Something went wrong.")),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (mode === "signin") login.mutate();
    else if (mode === "signup") register.mutate();
    else forgot.mutate();
  };

  return (
    <div className="grid h-full place-items-center bg-bg p-6">
      <div className="card auth-card max-w-md w-full p-8 space-y-6">
        <div className="text-center space-y-3">
          <Logo />
          <h1 className="text-2xl font-bold tracking-tight">An API client your team can share.</h1>
          <p className="text-sm muted">
            Build collections of REST endpoints. Every save is a versioned revision —
            roll back, audit, or restore any change. Invite teammates and stop
            emailing each other curl commands.
          </p>
        </div>

        <LoadingLinkButton
          href={googleStart}
          className="btn-neutral w-full justify-center"
          pendingLabel="Connecting to Google…"
          startIcon={<GoogleIcon />}
        >
          Continue with Google
        </LoadingLinkButton>

        <div className="flex items-center gap-3 text-xs muted-strong">
          <div className="flex-1 h-px bg-border" />
          or
          <div className="flex-1 h-px bg-border" />
        </div>

        {/* The mode-dependent block (form / forgot-sent confirmation +
            footer link row) is keyed so React remounts it whenever the
            mode flips. The .auth-mode class plays a quick fade-up so
            switching between sign-in / sign-up / forgot feels animated
            rather than swapped in place. */}
        <div
          key={`${mode}${forgotSent ? "-sent" : ""}${mfaNeeded ? "-mfa" : ""}`}
          className="auth-mode space-y-6"
        >
          {mode === "forgot" && forgotSent ? (
            <div className="text-sm text-center muted space-y-3">
              <p>If an account exists for <b>{email}</b>, a reset link is on its way.</p>
              <button className="text-accent-bright hover:underline" onClick={() => { setMode("signin"); setForgotSent(false); }}>
                Back to sign in
              </button>
            </div>
          ) : (
            <form className="space-y-3" onSubmit={submit}>
              {mode === "signup" && (
                <input
                  className="input"
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                />
              )}
              <input
                className="input"
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
              {mode !== "forgot" && (
                <PasswordInput
                  required
                  minLength={8}
                  placeholder={mode === "signup" ? "Choose a password (8+ chars)" : "Password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                />
              )}
              {mode === "signin" && mfaNeeded && (
                <input
                  className="input input-mono"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  placeholder="6-digit code (or recovery code)"
                  autoComplete="one-time-code"
                  inputMode="text"
                  autoFocus
                />
              )}
              {err && <p className="text-xs text-danger">{err}</p>}
              <LoadingButton
                type="submit"
                className="btn-primary w-full justify-center"
                pending={login.isPending || register.isPending || forgot.isPending}
                pendingLabel={mode === "signin" ? "Signing in…" : mode === "signup" ? "Creating…" : "Sending…"}
              >
                {mode === "signin" ? "Sign in" : mode === "signup" ? "Create account" : "Send reset link"}
              </LoadingButton>
            </form>
          )}

          <div className="flex items-center justify-between text-xs muted">
            {mode === "signin" && (
              <>
                <button onClick={() => { setMode("forgot"); setErr(null); }} className="hover:underline">Forgot password?</button>
                <button onClick={() => { setMode("signup"); setErr(null); }} className="hover:underline">
                  Need an account? <b className="font-semibold text-text">Sign up</b>
                </button>
              </>
            )}
            {mode === "signup" && (
              <>
                <span />
                <button onClick={() => { setMode("signin"); setErr(null); }} className="hover:underline">
                  Already have an account? <b className="font-semibold text-text">Sign in</b>
                </button>
              </>
            )}
            {mode === "forgot" && !forgotSent && (
              <>
                <span />
                <button onClick={() => { setMode("signin"); setErr(null); }} className="hover:underline">Back to sign in</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function parseErr(e: unknown, fallback: string): string {
  const m = String((e as Error).message ?? "");
  if (m.includes("email_taken")) return "An account with that email already exists.";
  if (m.includes("weak_password")) return "Password must be at least 8 characters.";
  if (m.includes("bad_credentials")) return "Wrong email or password.";
  if (m.includes("bad_email")) return "That doesn't look like a valid email.";
  return fallback;
}

function Logo() {
  return (
    <img src="/logo.png?v=2" alt="rest" className="mx-auto h-28 w-28" />
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.5 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.6 6 28.9 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C33.6 6.9 28.9 4.9 24 4.9 16.1 4.9 9.3 9 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.8-2 13.3-5.2l-6.1-5.2C29.2 35 26.8 36 24 36c-5.3 0-9.7-3.4-11.3-8.2l-6.6 5.1C9.1 39.8 16 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.6l6.1 5.2C40.7 35.6 44 30.2 44 24c0-1.2-.1-2.3-.4-3.5z"/>
    </svg>
  );
}
